import { Component, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Toast } from 'primeng/toast';
import { IPermissionList } from '../../../models/permission';
import { Permission } from '../../../services/permission';

@Component({
  selector: 'app-permissions',
  imports: [
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputTextModule,
    Dialog,
    FormsModule,
    SelectModule,
    Checkbox,
    Toast,
  ],
  templateUrl: './permissions.html',
  styleUrl: './permissions.css',
  providers: [MessageService],
})
export class Permissions implements OnInit {
  permissionService = inject(Permission);
  private messageService = inject(MessageService);
  permission!: IPermissionList;
  permissionDialog: boolean = false;
  submitted: boolean = false;

  permissionsWithCategory = computed(() => {
    const permissions = this.permissionService.permissions();
    const categories = this.permissionService.permissionCategories();

    return permissions.map((p) => ({
      ...p,
      categoryName: categories.find((c) => c.id === p.category_id)?.name ?? '',
    }));
  });

  permissionCategories = computed(() => {
    const categories = this.permissionService.permissionCategories();

    return categories.map((c) => ({
      label: c.name,
      value: c.id,
    }));
  });

  ngOnInit(): void {
    this.permissionService.getPermissionsList();
    this.permissionService.getPermissionGroupList();
    this.permissionService.getPermissionCategoryList();
  }

  editPermission(permission: IPermissionList) {
    this.permission = structuredClone(permission);
    this.permissionDialog = true;
  }

  hideDialog() {
    this.permissionDialog = false;
    this.permission = this.createEmptyPermission();
    this.submitted = false;
  }

  savePermission() {
    this.submitted = true;

    if (!this.permission.name.trim()) {
      return;
    }

    if (this.permission.id !== 0) {
      this.permissionService.updatePermission(this.permission).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successful',
            detail: 'Permission Updated',
            life: 3000,
          });

          this.afterSave();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Update failed',
            life: 3000,
          });
        },
      });
    } else {
      // new permission
      this.messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: 'Permission Created',
        life: 3000,
      });
    }

    this.permissionDialog = false;
    this.permission = this.createEmptyPermission();
  }

  private createEmptyPermission(): IPermissionList {
    return {
      id: 0,
      name: '',
      description: '',
      part_of_component: '',
      category_id: 0,
      group_ids: [],
    };
  }

  private afterSave() {
    this.permissionDialog = false;
    this.permission = this.createEmptyPermission();
  }
}
